import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { alpha } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import HeroWave from "../../../GlobalFunction/HeroWave";
import SuccessSubmitDialog from "../../../GlobalFunction/SuccessSubmit";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import { useAuth } from "../../Auth/AuthContext";
import RetailAPI from "../../../../api/Retail";
import { resolveWordPressUserID } from "../../../../Utils/UserIdentity";

const AREA_JSON_URL = "/files/CityCountyData.json";

const fonts = {
  ZH: "LINE Seed TW",
  EN: "LINE Seed Sans",
  JP: "LINE Seed JP",
};

const RETAIL_SUCCESS_LABELS = {
  reference: {
    ZH: "報修編號",
    EN: "Repair ID",
    JP: "修理受付番号",
  },

  close: {
    ZH: "關閉",
    EN: "Close",
    JP: "閉じる",
  },

  history: {
    ZH: "查看報修進度",
    EN: "View Repair Progress",
    JP: "修理状況を確認",
  },

  home: {
    ZH: "返回首頁",
    EN: "Back to Home",
    JP: "ホームへ戻る",
  },
};

const TEXT = {
  ZH: {
    breadcrumbHome: "首頁",
    breadcrumbMember: "會員專區",
    breadcrumbPage: "線上報修",

    pageTitle: "線上報修",

    intro: "請填寫以下欄位進行線上報修，未標示之商品請找就近服務站自行送修。",
    introNote:
      "提醒您，有加註 * 為必填欄位。線上報修所填寫的個人資料，僅作為商品維修之用。",

    sectionContact: "填寫聯絡資料",
    sectionProduct: "填寫維修商品資料",

    required: "【必填】",

    name: "報修人姓名",
    usageLocation: "使用據點／門市／廠別",
    endUser: "末端使用者",
    repairAvailableTime: "可維修時段",

    landline: "市話",
    landlineArea: "區碼",
    landlineNumber: "市內電話",

    mobile: "手機號碼",
    email: "Email",

    address: "通訊地址",
    city: "縣市",
    district: "鄉鎮市區",
    street: "街道路名",
    lane: "巷",
    alley: "弄",
    number: "號",
    floor: "樓",
    otherAddress: "其他",

    registeredProduct: "從我的註冊商品選擇",

    equipmentName: "設備名稱",
    equipmentNamePlaceholder: "請選擇設備名稱",
    customEquipmentNameOrModel: "請輸入設備名稱或型號",

    modelName: "設備型號",
    modelNamePlaceholder: "請選擇設備型號",
    serialNumber: "設備序號",

    purchaseDate: "採購日期",
    shippingDate: "出貨日期",
    installationDate: "安裝日期（啟用日期）",
    installer: "安裝單位／安裝人員",
    installationSource: "安裝來源",
    customInstallationSource: "請輸入安裝來源",

    faultReason: "選擇故障原因",
    customFaultReason: "請輸入故障原因",

    detail: "其他說明",
    detailPlaceholder:
      "請簡述商品異常狀況或使用情境，協助我們加速了解您遇到的問題",

    repairNotes: "備註",
    repairNotesPlaceholder: "請填寫特殊約定、維修注意事項或其他補充資訊",

    // addToMember: "我要同步加入會員（快速查看我的商品維修進度）",

    submit: "送出",

    notes: [
      "我們將依個資法妥善處理資料，詳見隱私權政策。",
      "回覆為個人郵件，未經同意請勿轉用。",
      "部分諮詢可能無法回覆或需較長時間處理。",
      "回覆時段：工作日 9:00–18:00。",
      "原則以電子郵件回覆，必要時可能改以電話聯絡。",
      "若信箱錯誤或系統故障，可能無法回覆。",
    ],

    agreeLabel: "我同意以上內容",
    agreeError: "請勾選同意以上內容",

    requiredError: "此欄為必填",
    emailError: "請輸入有效的 Email",

    registeredUnavailable: "目前尚未連接註冊商品資料。",

    submitting: "送出中…",
    submitSuccess: "報修申請已成功送出。報修編號：",
    submitError: "線上報修送出失敗，請稍後再試。",

    selectEquipmentFirst: "請先選擇設備名稱",
    noEquipmentModels: "此設備名稱尚未設定設備型號",
    noFaultReasons: "此設備名稱尚未設定故障原因",

    otherOption: "其他",
  },

  EN: {
    breadcrumbHome: "Home",
    breadcrumbMember: "Member Center",
    breadcrumbPage: "Online Repair",

    pageTitle: "Online Repair",

    intro:
      "Complete the form below to request an online repair. Products not listed should be delivered to your nearest service center.",
    introNote:
      "Fields marked with * are required. Personal information entered here will only be used for product repair service.",

    sectionContact: "Contact Information",
    sectionProduct: "Repair Product Information",

    required: "[Required]",

    name: "Repair Requester",
    usageLocation: "Usage Site / Store / Factory",
    endUser: "End User",
    repairAvailableTime: "Available Repair Time",

    landline: "Landline",
    landlineArea: "Area code",
    landlineNumber: "Telephone number",

    mobile: "Mobile Number",
    email: "Email",

    address: "Correspondence Address",
    city: "City / County",
    district: "District",
    street: "Street / Road",
    lane: "Lane",
    alley: "Alley",
    number: "No.",
    floor: "Floor",
    otherAddress: "Other",

    registeredProduct: "Select from my registered products",

    equipmentName: "Equipment Name",
    equipmentNamePlaceholder: "Select an equipment name",
    customEquipmentNameOrModel: "Enter the equipment name or model",

    modelName: "Equipment Model",
    modelNamePlaceholder: "Select an equipment model",
    serialNumber: "Equipment Serial Number",

    purchaseDate: "Purchase Date",
    shippingDate: "Shipping Date",
    installationDate: "Installation / Activation Date",
    installer: "Installation Company / Installer",
    installationSource: "Installation Source",
    customInstallationSource: "Enter the installation source",

    faultReason: "Fault Reason",
    customFaultReason: "Enter the fault reason",

    detail: "Additional Details",
    detailPlaceholder:
      "Describe the abnormal behavior or usage situation to help us understand the issue faster.",

    repairNotes: "Notes",
    repairNotesPlaceholder:
      "Enter special agreements, repair precautions, or other supplementary information.",

    // addToMember:
    //   "Add this product to my account so I can check repair progress quickly",

    submit: "Submit",

    notes: [
      "We will handle your data in accordance with privacy regulations.",
      "Replies are personal and should not be forwarded without permission.",
      "Some inquiries may require additional processing time or may not receive a reply.",
      "Reply hours: Weekdays 9:00–18:00.",
      "Replies will normally be sent by email, but telephone contact may be used when necessary.",
      "An incorrect email address or system issue may prevent us from replying.",
    ],

    agreeLabel: "I agree to the terms above",
    agreeError: "Please agree to the terms above",

    requiredError: "This field is required",
    emailError: "Enter a valid email address",

    registeredUnavailable: "Registered-product data is not connected yet.",

    submitting: "Submitting…",
    submitSuccess: "Your repair request was submitted successfully. Repair ID:",
    submitError:
      "Unable to submit the online repair request. Please try again later.",

    selectEquipmentFirst: "Select an equipment name first",
    noEquipmentModels: "No equipment models are configured for this equipment",
    noFaultReasons: "No fault reasons are configured for this equipment",

    otherOption: "Other",
  },

  JP: {
    breadcrumbHome: "ホーム",
    breadcrumbMember: "会員ページ",
    breadcrumbPage: "オンライン修理受付",

    pageTitle: "オンライン修理受付",

    intro:
      "以下の項目をご入力のうえ、オンライン修理をお申し込みください。対象外の商品は最寄りのサービス拠点へお持ち込みください。",
    introNote:
      "* の付いた項目は必須です。入力された個人情報は、商品修理の目的にのみ使用します。",

    sectionContact: "連絡先情報",
    sectionProduct: "修理商品情報",

    required: "【必須】",

    name: "修理依頼者名",
    usageLocation: "使用拠点／店舗／工場",
    endUser: "エンドユーザー",
    repairAvailableTime: "修理対応可能時間",

    landline: "固定電話",
    landlineArea: "市外局番",
    landlineNumber: "電話番号",

    mobile: "携帯電話番号",
    email: "メールアドレス",

    address: "連絡先住所",
    city: "都道府県",
    district: "市区町村",
    street: "町名・道路名",
    lane: "巷",
    alley: "弄",
    number: "番地",
    floor: "階",
    otherAddress: "その他",

    registeredProduct: "登録済み商品から選択",

    equipmentName: "設備名",
    equipmentNamePlaceholder: "設備名を選択してください",
    customEquipmentNameOrModel: "設備名または型式を入力してください",

    modelName: "設備型式",
    modelNamePlaceholder: "設備型式を選択してください",
    serialNumber: "設備シリアル番号",

    purchaseDate: "購入日",
    shippingDate: "出荷日",
    installationDate: "設置日（使用開始日）",
    installer: "設置会社／設置担当者",
    installationSource: "設置元",
    customInstallationSource: "設置元を入力してください",

    faultReason: "故障原因",
    customFaultReason: "故障原因を入力してください",

    detail: "その他の説明",
    detailPlaceholder:
      "商品の異常な状態や使用状況をご記入ください。問題の把握に役立ちます。",

    repairNotes: "備考",
    repairNotesPlaceholder:
      "特別な取り決め、修理時の注意事項、その他の補足情報を入力してください。",

    // addToMember: "会員情報に追加して修理状況を確認する",

    submit: "送信する",

    notes: [
      "個人情報保護法に基づき、入力された情報を適切に取り扱います。",
      "返信内容は個人宛てのものです。許可なく転載・転用しないでください。",
      "内容によっては、回答に時間を要する場合や回答できない場合があります。",
      "回答時間：平日 9:00〜18:00。",
      "原則としてメールで回答しますが、必要に応じて電話で連絡する場合があります。",
      "メールアドレスの誤りやシステム障害により、返信できない場合があります。",
    ],

    agreeLabel: "上記内容に同意します",
    agreeError: "上記内容への同意が必要です",

    requiredError: "必須項目です",
    emailError: "有効なメールアドレスを入力してください",

    registeredUnavailable: "登録商品のデータはまだ接続されていません。",

    submitting: "送信中…",
    submitSuccess: "修理依頼を送信しました。修理受付番号：",
    submitError:
      "オンライン修理依頼を送信できませんでした。しばらくしてからもう一度お試しください。",

    selectEquipmentFirst: "先に設備名を選択してください",
    noEquipmentModels: "この設備には型式が設定されていません",
    noFaultReasons: "この設備には故障原因が設定されていません",

    otherOption: "その他",
  },
};

const STANDARD_EQUIPMENT_FAULT_REASONS = [
  {
    value: "sensor_problem",
    ZH: "感應不良",
    EN: "Sensor malfunction",
    JP: "センサー不良",
  },
  {
    value: "indicator_light_problem",
    ZH: "燈號異常",
    EN: "Indicator light malfunction",
    JP: "表示ランプの異常",
  },
  {
    value: "no_response_with_power_or_battery",
    ZH: "插電或已裝電池無反應",
    EN: "No response when plugged in or with batteries installed",
    JP: "電源接続または電池装着後も反応しない",
  },
  {
    value: "damaged_outer_case",
    ZH: "外殼受損",
    EN: "Damaged outer casing",
    JP: "外装ケースの損傷",
  },
  {
    value: "damaged_battery_box",
    ZH: "電池盒受損",
    EN: "Damaged battery compartment",
    JP: "電池ボックスの損傷",
  },
  {
    value: "damaged_power_plug",
    ZH: "插頭受損",
    EN: "Damaged power plug",
    JP: "電源プラグの損傷",
  },
  {
    value: "damaged_power_adapter",
    ZH: "變壓器受損",
    EN: "Damaged power adapter",
    JP: "電源アダプターの損傷",
  },
  {
    value: "equipment_moisture_damage",
    ZH: "機台受潮",
    EN: "Equipment affected by moisture",
    JP: "機器の湿気・水分による異常",
  },
  {
    value: "damaged_tray",
    ZH: "托盤受損",
    EN: "Damaged tray",
    JP: "トレーの損傷",
  },
  {
    value: "missing_parts",
    ZH: "零件缺少",
    EN: "Missing parts",
    JP: "部品の欠品",
  },
  {
    value: "missing_key",
    ZH: "鑰匙遺失",
    EN: "Missing key",
    JP: "鍵の紛失",
  },
  {
    value: "dirty_equipment",
    ZH: "機台髒污",
    EN: "Dirty equipment",
    JP: "機器の汚れ",
  },
  {
    value: "detergent_not_dispensed",
    ZH: "洗劑無法正常抽取",
    EN: "Detergent cannot be dispensed normally",
    JP: "洗剤を正常に吸い上げられない",
  },
  {
    value: "other_problem",
    ZH: "其他（請輸入問題）",
    EN: "Other (enter the problem)",
    JP: "その他（問題を入力してください）",
  },
];

const INSECT_LIGHT_FAULT_REASONS = [
  {
    value: "lamp_not_lighting",
    ZH: "燈管不亮",
    EN: "Lamp does not light",
    JP: "ランプが点灯しない",
  },
  {
    value: "lamp_flickering",
    ZH: "燈管閃爍",
    EN: "Lamp flickers",
    JP: "ランプが点滅する",
  },
  {
    value: "equipment_overheating",
    ZH: "機身發燙",
    EN: "Equipment body becomes hot",
    JP: "本体が過熱する",
  },
  {
    value: "abnormal_noise",
    ZH: "異音",
    EN: "Abnormal noise",
    JP: "異音",
  },
  {
    value: "missing_parts",
    ZH: "零件缺少",
    EN: "Missing parts",
    JP: "部品の欠品",
  },
  {
    value: "other_problem",
    ZH: "其他（請輸入問題）",
    EN: "Other (enter the problem)",
    JP: "その他（問題を入力してください）",
  },
];

const OTHER_EQUIPMENT_FAULT_REASONS = [
  {
    value: "other_problem",
    ZH: "其他（請輸入問題）",
    EN: "Other (enter the problem)",
    JP: "その他（問題を入力してください）",
  },
];

const EQUIPMENT_OPTIONS = [
  {
    value: "soap_dispenser",
    ZH: "給皂機",
    EN: "Soap Dispenser",
    JP: "ソープディスペンサー",
    models: [
      "給皂機UD-1600",
      "給皂機MD-1600",
      "給皂機GMD-500F",
      "給皂機UD-8600",
      "給皂機UD-450",
      "給皂機GUD-500",
    ],
    faultReasons: STANDARD_EQUIPMENT_FAULT_REASONS,
  },
  {
    value: "sanitizer_dispenser",
    ZH: "消毒機",
    EN: "Sanitizer Dispenser",
    JP: "消毒液ディスペンサー",
    models: [
      "消毒機UD-1600",
      "消毒機MD-1600",
      "消毒機GUD-1000",
      "消毒機HDI-2020",
      "消毒機GMD-500A",
      "消毒機UD-8600",
      "消毒機UD-450",
      "消毒機GUD-500",
    ],
    faultReasons: STANDARD_EQUIPMENT_FAULT_REASONS,
  },
  {
    value: "dyson",
    ZH: "dyson",
    EN: "dyson",
    JP: "dyson",
    models: ["dyson乾手機AB14", "dyson水龍頭AB09", "dyson V型乾手機"],
    faultReasons: STANDARD_EQUIPMENT_FAULT_REASONS,
  },
  {
    value: "equipment",
    ZH: "設備",
    EN: "Equipment",
    JP: "設備",
    models: [
      "設備WS-3000F",
      "設備HD-3000自動手指消毒供給裝置",
      "設備SD-3000F自動泡沫洗手供給裝置",
    ],
    faultReasons: STANDARD_EQUIPMENT_FAULT_REASONS,
  },
  {
    value: "insect_light",
    ZH: "捕蟲燈",
    EN: "Insect Light",
    JP: "捕虫器",
    models: ["捕蟲燈MK-2000", "MK-2020攔截者(鐵灰)", "MK-2020攔截者(白)"],
    faultReasons: INSECT_LIGHT_FAULT_REASONS,
  },
  {
    value: "other",
    ZH: "其他",
    EN: "Other",
    JP: "その他",
    models: [],
    faultReasons: OTHER_EQUIPMENT_FAULT_REASONS,
  },
];

const INSTALLATION_SOURCE_OPTIONS = [
  {
    value: "manufacturer",
    ZH: "原廠",
    EN: "Manufacturer",
    JP: "メーカー",
  },
  {
    value: "self_installed",
    ZH: "自行安裝",
    EN: "Self-installed",
    JP: "自己設置",
  },
  {
    value: "distributor",
    ZH: "經銷商",
    EN: "Distributor",
    JP: "販売代理店",
  },
  {
    value: "contractor",
    ZH: "工程商",
    EN: "Contractor",
    JP: "施工業者",
  },
  {
    value: "other",
    ZH: "其他",
    EN: "Other",
    JP: "その他",
  },
];

const INITIAL_FORM = {
  name: "",
  usageLocation: "",
  endUser: "",
  repairAvailableTime: "",

  landlineArea: "",
  landlineNumber: "",

  mobile: "",
  email: "",

  city: "",
  district: "",
  street: "",
  lane: "",
  alley: "",
  number: "",
  floor: "",
  otherAddress: "",

  equipmentName: "",
  customEquipmentNameOrModel: "",

  modelName: "",
  serialNumber: "",

  purchaseDate: "",
  shippingDate: "",
  installationDate: "",

  installer: "",
  installationSource: "",
  customInstallationSource: "",

  faultReason: "",
  customFaultReason: "",

  repairNotes: "",

  // addToMember: true,
  agree: false,
};

const normalizeLang = (value) => {
  const code = String(value || "")
    .trim()
    .toUpperCase();

  if (code === "EN" || code === "JP") {
    return code;
  }

  return "ZH";
};

const digitsOnly = (value) => String(value || "").replace(/\D+/g, "");

const pickMemberData = (user) => {
  const firstName = String(user?.firstName || "").trim();
  const lastName = String(user?.lastName || "").trim();
  const fallbackName = String(user?.name || "").trim();

  return {
    name: [lastName, firstName].filter(Boolean).join("") || fallbackName,

    mobile: String(user?.phoneNumber || user?.phone || "").trim(),

    email: String(user?.email || "").trim(),
  };
};

const SectionTitle = ({ number, children, accent, lang }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      px: { xs: 1.5, md: 2 },
      py: 1.5,
      borderBottom: "1px solid",
      borderColor: "divider",
      bgcolor: alpha(accent, 0.045),
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        bgcolor: accent,
        color: "common.white",
        fontWeight: 700,
      }}
    >
      {number}
    </Box>

    <Typography
      sx={{
        fontFamily: fonts[lang],
        fontWeight: 700,
        fontSize: { xs: 18, sm: 20 },
        color: accent,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const LabelCell = ({ children, required, requiredLabel, lang }) => (
  <Box
    sx={(theme) => ({
      bgcolor: theme.palette.mode === "light" ? "grey.50" : alpha("#fff", 0.04),

      borderRight: {
        md: `1px solid ${theme.palette.divider}`,
      },

      p: 1.25,
      display: "flex",
      alignItems: "center",
      minHeight: 56,
    })}
  >
    <Typography
      sx={{
        fontSize: {
          xs: 14,
          sm: 16,
          lg: 14,
        },
        fontWeight: 700,
        fontFamily: fonts[lang],
      }}
    >
      {children}{" "}
      {required && (
        <Typography
          component="span"
          color="error.main"
          sx={{
            fontWeight: 700,
            fontFamily: fonts[lang],
          }}
        >
          {requiredLabel}
        </Typography>
      )}
    </Typography>
  </Box>
);

const Row = ({ label, required, requiredLabel, children, lang }) => (
  <Box
    sx={(theme) => ({
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: "240px 1fr",
      },

      borderBottom: `1px solid ${theme.palette.divider}`,
    })}
  >
    <LabelCell required={required} requiredLabel={requiredLabel} lang={lang}>
      {label}
    </LabelCell>

    <Box sx={{ p: 1.25 }}>{children}</Box>
  </Box>
);

export default function Retail({ mode = "b2b" }) {
  const accent = getPrimaryColor(mode);
  const navigate = useNavigate();

  const { user, loading: authLoading } = useAuth();

  const userID = useMemo(() => resolveWordPressUserID(user) || null, [user]);

  const [lang, setLang] = useState(() => normalizeLang(getLangFromCookie()));

  const text = TEXT[lang] || TEXT.ZH;

  const [cityData, setCityData] = useState([]);

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  const [notice, setNotice] = useState("");
  const [noticeSeverity, setNoticeSeverity] = useState("info");

  const [submitting, setSubmitting] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);

  const [submittedRetailID, setSubmittedRetailID] = useState("");

  const noticeRef = useRef(null);

  useEffect(() => {
    const syncLanguage = () => {
      setLang(normalizeLang(getLangFromCookie()));
      setNotice("");
      setNoticeSeverity("info");
    };

    syncLanguage();

    window.addEventListener("app:languageChanged", syncLanguage);

    return () => {
      window.removeEventListener("app:languageChanged", syncLanguage);
    };
  }, []);

  useEffect(() => {
    if (!notice) {
      return;
    }

    noticeRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [notice]);

  useEffect(() => {
    const profile = pickMemberData(user);

    setForm((previous) => ({
      ...previous,
      name: previous.name || profile.name,
      mobile: previous.mobile || profile.mobile,
      email: previous.email || profile.email,
    }));
  }, [user]);

  useEffect(() => {
    let active = true;

    async function loadCityData() {
      try {
        const response = await fetch(AREA_JSON_URL);
        const data = await response.json();

        if (!active) {
          return;
        }

        const list = Array.isArray(data)
          ? data
          : data?.cities || data?.CityList || [];

        setCityData(list);
      } catch (error) {
        console.error("Failed to load CityCountyData:", error);
      }
    }

    loadCityData();

    return () => {
      active = false;
    };
  }, []);

  const cityOptions = useMemo(
    () =>
      cityData.map((city) => {
        const cityZH = city?.cityname || city?.CityName || "";

        const cityEN = city?.cityengname || city?.CityEngName || cityZH;

        const rawAreas = city?.area || city?.AreaList || city?.areas || [];

        return {
          id: cityZH,
          label: lang === "EN" ? cityEN : cityZH,

          areas: rawAreas.map((area) => {
            const areaZH = area?.areaname || area?.AreaName || "";

            const areaEN = area?.areaengname || area?.AreaEngName || areaZH;

            return {
              id: areaZH,
              zh: areaZH,
              en: areaEN,
            };
          }),
        };
      }),
    [cityData, lang],
  );

  const currentCity = useMemo(
    () => cityOptions.find((city) => city.id === form.city) || null,
    [cityOptions, form.city],
  );

  const districtOptions = currentCity?.areas || [];

  const equipmentOptions = useMemo(
    () =>
      EQUIPMENT_OPTIONS.map((equipment) => ({
        ...equipment,
        label: equipment[lang] || equipment.ZH,
      })),
    [lang],
  );

  const currentEquipment = useMemo(
    () =>
      equipmentOptions.find(
        (equipment) => equipment.value === form.equipmentName,
      ) || null,
    [equipmentOptions, form.equipmentName],
  );

  const equipmentModelOptions = useMemo(
    () =>
      Array.isArray(currentEquipment?.models) ? currentEquipment.models : [],
    [currentEquipment],
  );

  const currentEquipmentModel = useMemo(
    () =>
      equipmentModelOptions.find((model) => model === form.modelName) || null,
    [equipmentModelOptions, form.modelName],
  );

  const faultReasonOptions = useMemo(
    () =>
      (Array.isArray(currentEquipment?.faultReasons)
        ? currentEquipment.faultReasons
        : []
      ).map((reason) => ({
        ...reason,
        label: reason[lang] || reason.ZH,
      })),
    [currentEquipment, lang],
  );

  const currentFaultReason = useMemo(
    () =>
      faultReasonOptions.find((reason) => reason.value === form.faultReason) ||
      null,
    [faultReasonOptions, form.faultReason],
  );

  const installationSourceOptions = useMemo(
    () =>
      INSTALLATION_SOURCE_OPTIONS.map((source) => ({
        ...source,
        label: source[lang] || source.ZH,
      })),
    [lang],
  );

  const currentInstallationSource = useMemo(
    () =>
      installationSourceOptions.find(
        (source) => source.value === form.installationSource,
      ) || null,
    [installationSourceOptions, form.installationSource],
  );

  const isOtherEquipment = form.equipmentName === "other";

  const isOtherFaultReason = form.faultReason === "other_problem";

  const isOtherInstallationSource = form.installationSource === "other";

  const update = (key) => (event) => {
    const value = event.target.value;

    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [key]: undefined,
    }));
  };

  const updateDigits = (key) => (event) => {
    const value = digitsOnly(event.target.value);

    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [key]: undefined,
    }));
  };

  const handleEquipmentNameChange = (event, nextEquipment) => {
    const equipmentValue = String(nextEquipment?.value || "").trim();

    setForm((previous) => ({
      ...previous,
      equipmentName: equipmentValue,
      customEquipmentNameOrModel: "",
      modelName: "",
      faultReason: "",
      customFaultReason: "",
    }));

    setErrors((previous) => ({
      ...previous,
      equipmentName: undefined,
      customEquipmentNameOrModel: undefined,
      modelName: undefined,
      faultReason: undefined,
      customFaultReason: undefined,
    }));
  };

  const handleEquipmentModelChange = (event, nextModel) => {
    setForm((previous) => ({
      ...previous,
      modelName: String(nextModel || "").trim(),
    }));

    setErrors((previous) => ({
      ...previous,
      modelName: undefined,
    }));
  };

  const handleFaultReasonChange = (event, nextReason) => {
    const reasonValue = String(nextReason?.value || "").trim();

    setForm((previous) => ({
      ...previous,
      faultReason: reasonValue,
      customFaultReason: "",
    }));

    setErrors((previous) => ({
      ...previous,
      faultReason: undefined,
      customFaultReason: undefined,
    }));
  };

  const handleInstallationSourceChange = (event, nextSource) => {
    const sourceValue = String(nextSource?.value || "").trim();

    setForm((previous) => ({
      ...previous,
      installationSource: sourceValue,
      customInstallationSource: "",
    }));

    setErrors((previous) => ({
      ...previous,
      installationSource: undefined,
      customInstallationSource: undefined,
    }));
  };

  const validate = () => {
    const nextErrors = {};

    [
      "name",
      "email",
      "equipmentName",
      "serialNumber",
      "installationDate",
      "faultReason",
    ].forEach((key) => {
      if (!String(form[key] || "").trim()) {
        nextErrors[key] = "required";
      }
    });

    if (isOtherEquipment) {
      if (!String(form.customEquipmentNameOrModel || "").trim()) {
        nextErrors.customEquipmentNameOrModel = "required";
      }
    } else if (!String(form.modelName || "").trim()) {
      nextErrors.modelName = "required";
    }

    if (isOtherFaultReason && !String(form.customFaultReason || "").trim()) {
      nextErrors.customFaultReason = "required";
    }

    if (
      isOtherInstallationSource &&
      !String(form.customInstallationSource || "").trim()
    ) {
      nextErrors.customInstallationSource = "required";
    }

    if (!form.agree) {
      nextErrors.agree = "agree";
    }

    const normalizedEmail = String(form.email || "").trim();

    if (
      normalizedEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
    ) {
      nextErrors.email = "email";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const errorText = (key) => {
    const code = errors[key];

    if (code === "email") {
      return text.emailError;
    }

    if (code === "agree") {
      return text.agreeError;
    }

    if (code === "required") {
      return text.requiredError;
    }

    return "";
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    setSubmittedRetailID("");
  };

  const handleSubmit = async () => {
    if (submitting || authLoading) {
      return;
    }

    setNotice("");
    setNoticeSeverity("info");

    if (!validate()) {
      return;
    }

    setSubmitting(true);

    try {
      const result = await RetailAPI.create({
        userID,

        /* ---------------- person/contact ---------------- */

        name: form.name,
        usageLocation: form.usageLocation,
        endUser: form.endUser,
        repairAvailableTime: form.repairAvailableTime,

        landlineAreaCode: form.landlineArea,

        landlinePhoneNumber: form.landlineNumber,

        mobileNumber: form.mobile,
        email: form.email,

        city: form.city,
        district: form.district,
        street: form.street,
        lane: form.lane,
        alley: form.alley,
        streetNumber: form.number,
        floor: form.floor,
        otherAddress: form.otherAddress,

        addToMember: form.addToMember,

        /* ---------------- repair information ---------------- */

        equipmentName: form.equipmentName,

        customEquipmentNameOrModel: form.customEquipmentNameOrModel,

        modelName: form.modelName,
        serialNumber: form.serialNumber,

        purchaseDate: form.purchaseDate,
        shippingDate: form.shippingDate,
        installationDate: form.installationDate,

        installer: form.installer,

        installationSource: form.installationSource,

        customInstallationSource: form.customInstallationSource,

        faultReason: form.faultReason,

        customFaultReason: form.customFaultReason,

        repairNotes: form.repairNotes,
      });

      if (result?.ok !== true || !String(result?.retailID || "").trim()) {
        throw new Error("The Retail API returned an invalid response.");
      }

      const submittedID = String(result.retailID).trim();

      const memberData = pickMemberData(user);

      setForm({
        ...INITIAL_FORM,

        name: memberData.name,
        mobile: memberData.mobile,
        email: memberData.email,
      });

      setErrors({});

      setNotice("");
      setNoticeSeverity("info");

      setSubmittedRetailID(submittedID);
      setSuccessOpen(true);
    } catch (error) {
      console.error("Failed to submit Retail request:", error);

      const backendField = String(error?.response?.data?.field || "").trim();

      if (
        backendField &&
        Object.prototype.hasOwnProperty.call(INITIAL_FORM, backendField)
      ) {
        setErrors((previous) => ({
          ...previous,

          [backendField]: backendField === "email" ? "email" : "required",
        }));
      }

      setNoticeSeverity("error");
      setNotice(text.submitError);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = {
    "& .MuiInputBase-input": {
      fontFamily: fonts[lang],
      fontSize: {
        xs: 16,
        sm: 17,
        lg: 16,
      },
    },

    "& .MuiInputLabel-root": {
      fontFamily: fonts[lang],
      fontSize: {
        xs: 16,
        sm: 17,
        lg: 16,
      },
    },

    "& .MuiFormHelperText-root": {
      fontFamily: fonts[lang],
      fontSize: {
        xs: 12,
        sm: 14,
        lg: 12,
      },
    },
  };

  const tfCommon = {
    fullWidth: true,
    size: "small",
    sx: fieldSx,
  };

  const sideBySideSx = {
    width: "100%",

    "& > *": {
      width: "100%",
      minWidth: 0,
    },
  };

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <HeroWave mode={mode} />

      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link
            component={RouterLink}
            to="/首頁"
            underline="hover"
            sx={{ color: "text.secondary" }}
          >
            {text.breadcrumbHome}
          </Link>

          <Link
            component={RouterLink}
            to="/profile"
            underline="hover"
            sx={{ color: "text.secondary" }}
          >
            {text.breadcrumbMember}
          </Link>

          <Typography color="text.primary">{text.breadcrumbPage}</Typography>
        </Breadcrumbs>
      </Container>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          maxWidth: 1100,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontFamily: fonts[lang],
            fontWeight: 700,
            fontSize: {
              xs: 30,
              sm: 36,
              md: 40,
            },
            mb: 2,
            textAlign: "center",
          }}
        >
          {text.pageTitle}
        </Typography>

        <Box
          sx={{
            maxWidth: 860,
            mx: "auto",
            mb: 3,
            fontFamily: fonts[lang],
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: 16,
                sm: 18,
                lg: 16,
              },
              lineHeight: 1.8,
            }}
          >
            {text.intro}
          </Typography>

          <Typography
            sx={{
              fontSize: {
                xs: 16,
                sm: 18,
                lg: 16,
              },
              lineHeight: 1.8,
            }}
          >
            {text.introNote}
          </Typography>
        </Box>

        {!!notice && (
          <Alert
            ref={noticeRef}
            severity={noticeSeverity}
            onClose={() => setNotice("")}
            sx={{
              mb: 2,
              fontFamily: fonts[lang],
            }}
          >
            {notice}
          </Alert>
        )}

        <Paper
          component="form"
          onSubmit={(event) => event.preventDefault()}
          variant="outlined"
          sx={{
            borderRadius: 1.5,
            overflow: "hidden",
            mb: 4,
          }}
        >
          <SectionTitle number="1" accent={accent} lang={lang}>
            {text.sectionContact}
          </SectionTitle>

          <Row
            label={text.name}
            required
            requiredLabel={text.required}
            lang={lang}
          >
            <TextField
              {...tfCommon}
              value={form.name}
              onChange={update("name")}
              error={!!errors.name}
              helperText={errorText("name")}
            />
          </Row>

          <Row label={text.usageLocation} lang={lang}>
            <TextField
              {...tfCommon}
              value={form.usageLocation}
              onChange={update("usageLocation")}
            />
          </Row>

          <Row label={text.endUser} lang={lang}>
            <TextField
              {...tfCommon}
              value={form.endUser}
              onChange={update("endUser")}
            />
          </Row>

          <Row label={text.landline} lang={lang}>
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={1}
              sx={sideBySideSx}
            >
              <TextField
                {...tfCommon}
                value={form.landlineArea}
                onChange={updateDigits("landlineArea")}
                placeholder={text.landlineArea}
                sx={{
                  ...fieldSx,
                  width: {
                    xs: "100%",
                    sm: 180,
                  },
                  flexShrink: 0,
                }}
              />

              <TextField
                {...tfCommon}
                value={form.landlineNumber}
                onChange={updateDigits("landlineNumber")}
                placeholder={text.landlineNumber}
              />
            </Stack>
          </Row>

          <Row label={text.mobile} lang={lang}>
            <TextField
              {...tfCommon}
              value={form.mobile}
              onChange={updateDigits("mobile")}
            />
          </Row>

          <Row
            label={text.email}
            required
            requiredLabel={text.required}
            lang={lang}
          >
            <TextField
              {...tfCommon}
              required
              type="email"
              value={form.email}
              onChange={update("email")}
              error={!!errors.email}
              helperText={errorText("email")}
            />
          </Row>

          <Row label={text.address} lang={lang}>
            <Stack spacing={1.25}>
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={1}
                sx={sideBySideSx}
              >
                <TextField
                  {...tfCommon}
                  select
                  value={form.city}
                  onChange={(event) => {
                    const value = event.target.value;

                    setForm((previous) => ({
                      ...previous,
                      city: value,
                      district: "",
                    }));
                  }}
                  label={text.city}
                >
                  {cityOptions.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  {...tfCommon}
                  select
                  value={form.district}
                  onChange={update("district")}
                  label={text.district}
                  disabled={!form.city}
                >
                  {districtOptions.map((district) => (
                    <MenuItem key={district.id} value={district.id}>
                      {lang === "EN" ? district.en : district.zh}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <TextField
                {...tfCommon}
                value={form.street}
                onChange={update("street")}
                label={text.street}
              />

              <Box
                sx={{
                  display: {
                    xs: "grid",
                    sm: "block",
                  },
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: {
                    xs: 1,
                    sm: 0,
                  },

                  "& > *": {
                    minWidth: 0,
                  },
                }}
              >
                <Box
                  sx={{
                    display: {
                      xs: "contents",
                      sm: "flex",
                    },
                    width: "100%",
                    gap: {
                      sm: 1,
                    },
                    mb: {
                      sm: 1.25,
                    },

                    "& > *": {
                      minWidth: 0,
                    },
                  }}
                >
                  <TextField
                    {...tfCommon}
                    value={form.lane}
                    onChange={updateDigits("lane")}
                    label={text.lane}
                  />

                  <TextField
                    {...tfCommon}
                    value={form.alley}
                    onChange={updateDigits("alley")}
                    label={text.alley}
                  />

                  <TextField
                    {...tfCommon}
                    value={form.number}
                    onChange={update("number")}
                    label={text.number}
                  />
                </Box>

                <Box
                  sx={{
                    display: {
                      xs: "contents",
                      sm: "flex",
                    },
                    width: "100%",
                    gap: {
                      sm: 1,
                    },

                    "& > *": {
                      minWidth: 0,
                    },
                  }}
                >
                  <TextField
                    {...tfCommon}
                    value={form.floor}
                    onChange={update("floor")}
                    label={text.floor}
                    sx={{
                      ...fieldSx,
                      maxWidth: {
                        xs: "none",
                        sm: 260,
                      },
                    }}
                  />

                  <TextField
                    {...tfCommon}
                    value={form.otherAddress}
                    onChange={update("otherAddress")}
                    label={text.otherAddress}
                    sx={{
                      ...fieldSx,
                      gridColumn: {
                        xs: "1 / -1",
                        sm: "auto",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Stack>
          </Row>

          <Row label={text.repairAvailableTime} lang={lang}>
            <TextField
              {...tfCommon}
              value={form.repairAvailableTime}
              onChange={update("repairAvailableTime")}
            />
          </Row>

          <SectionTitle number="2" accent={accent} lang={lang}>
            {text.sectionProduct}
          </SectionTitle>

          {!authLoading && !user && (
            <Box
              sx={{
                px: { xs: 1.5, md: 2 },
                py: 1.25,
                display: "flex",
                justifyContent: "flex-end",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Button
                type="button"
                variant="text"
                onClick={() =>
                  navigate("/auth", {
                    state: {
                      redirectTo: "/線上報修",
                    },
                  })
                }
                sx={{
                  fontFamily: fonts[lang],
                  fontWeight: 700,
                }}
              >
                {text.registeredProduct}
              </Button>
            </Box>
          )}

          <Row
            label={text.equipmentName}
            required
            requiredLabel={text.required}
            lang={lang}
          >
            <Autocomplete
              options={equipmentOptions}
              value={currentEquipment}
              onChange={handleEquipmentNameChange}
              getOptionLabel={(option) => String(option?.label || "")}
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  placeholder={text.equipmentNamePlaceholder}
                  error={!!errors.equipmentName}
                  helperText={errorText("equipmentName")}
                />
              )}
            />
          </Row>

          <Row
            label={
              isOtherEquipment
                ? text.customEquipmentNameOrModel
                : text.modelName
            }
            required
            requiredLabel={text.required}
            lang={lang}
          >
            {isOtherEquipment ? (
              <TextField
                {...tfCommon}
                value={form.customEquipmentNameOrModel}
                onChange={update("customEquipmentNameOrModel")}
                placeholder={text.customEquipmentNameOrModel}
                error={!!errors.customEquipmentNameOrModel}
                helperText={errorText("customEquipmentNameOrModel")}
              />
            ) : (
              <Autocomplete
                options={equipmentModelOptions}
                value={currentEquipmentModel}
                onChange={handleEquipmentModelChange}
                disabled={!form.equipmentName}
                getOptionLabel={(option) => String(option || "")}
                isOptionEqualToValue={(option, value) => option === value}
                noOptionsText={
                  form.equipmentName
                    ? text.noEquipmentModels
                    : text.selectEquipmentFirst
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    placeholder={
                      form.equipmentName
                        ? text.modelNamePlaceholder
                        : text.selectEquipmentFirst
                    }
                    error={!!errors.modelName}
                    helperText={errorText("modelName")}
                  />
                )}
              />
            )}
          </Row>

          <Row
            label={text.serialNumber}
            required
            requiredLabel={text.required}
            lang={lang}
          >
            <TextField
              {...tfCommon}
              value={form.serialNumber}
              onChange={update("serialNumber")}
              error={!!errors.serialNumber}
              helperText={errorText("serialNumber")}
            />
          </Row>

          <Row label={text.purchaseDate} lang={lang}>
            <TextField
              {...tfCommon}
              type="date"
              value={form.purchaseDate}
              onChange={update("purchaseDate")}
            />
          </Row>

          <Row label={text.shippingDate} lang={lang}>
            <TextField
              {...tfCommon}
              type="date"
              value={form.shippingDate}
              onChange={update("shippingDate")}
            />
          </Row>

          <Row
            label={text.installationDate}
            required
            requiredLabel={text.required}
            lang={lang}
          >
            <TextField
              {...tfCommon}
              type="date"
              value={form.installationDate}
              onChange={update("installationDate")}
              error={!!errors.installationDate}
              helperText={errorText("installationDate")}
            />
          </Row>

          <Row label={text.installer} lang={lang}>
            <TextField
              {...tfCommon}
              value={form.installer}
              onChange={update("installer")}
            />
          </Row>

          <Row label={text.installationSource} lang={lang}>
            <Stack spacing={1}>
              <Autocomplete
                options={installationSourceOptions}
                value={currentInstallationSource}
                onChange={handleInstallationSourceChange}
                getOptionLabel={(option) => String(option?.label || "")}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    error={!!errors.installationSource}
                    helperText={errorText("installationSource")}
                  />
                )}
              />

              {isOtherInstallationSource && (
                <TextField
                  {...tfCommon}
                  value={form.customInstallationSource}
                  onChange={update("customInstallationSource")}
                  placeholder={text.customInstallationSource}
                  error={!!errors.customInstallationSource}
                  helperText={errorText("customInstallationSource")}
                />
              )}
            </Stack>
          </Row>

          <Row
            label={text.faultReason}
            required
            requiredLabel={text.required}
            lang={lang}
          >
            <Stack spacing={1}>
              <Autocomplete
                options={faultReasonOptions}
                value={currentFaultReason}
                onChange={handleFaultReasonChange}
                disabled={!form.equipmentName}
                getOptionLabel={(option) => String(option?.label || "")}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                noOptionsText={
                  form.equipmentName
                    ? text.noFaultReasons
                    : text.selectEquipmentFirst
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    placeholder={
                      form.equipmentName ? "" : text.selectEquipmentFirst
                    }
                    error={!!errors.faultReason}
                    helperText={errorText("faultReason")}
                  />
                )}
              />
            </Stack>
          </Row>

          {isOtherFaultReason && (
            <Row
              label={text.detail}
              required
              requiredLabel={text.required}
              lang={lang}
            >
              <TextField
                {...tfCommon}
                multiline
                minRows={5}
                value={form.customFaultReason}
                onChange={update("customFaultReason")}
                placeholder={text.detailPlaceholder}
                error={!!errors.customFaultReason}
                helperText={errorText("customFaultReason")}
              />
            </Row>
          )}

          <Row label={text.repairNotes} lang={lang}>
            <TextField
              {...tfCommon}
              multiline
              minRows={4}
              value={form.repairNotes}
              onChange={update("repairNotes")}
              placeholder={text.repairNotesPlaceholder}
            />
          </Row>

          {/* <Row label="" lang={lang}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.addToMember}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      addToMember: event.target.checked,
                    }))
                  }
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: fonts[lang],
                    fontWeight: 600,
                  }}
                >
                  {text.addToMember}
                </Typography>
              }
            />
          </Row> */}
        </Paper>

        {/* Partner-style terms notice */}
        <Paper
          variant="outlined"
          sx={{
            p: {
              xs: 2,
              md: 3,
            },
            mb: 2,
            borderRadius: 1.5,
            borderColor: alpha(accent, 0.28),
            bgcolor: alpha("#fff", 0.92),
          }}
        >
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2.5,
              display: "grid",
              gap: 0.75,
              lineHeight: 1.8,
            }}
          >
            {text.notes.map((note, index) => (
              <Typography
                component="li"
                key={`${lang}-retail-note-${index}`}
                sx={{
                  fontFamily: fonts[lang],
                  fontSize: {
                    xs: 14,
                    sm: 16,
                    lg: 15,
                  },
                  lineHeight: 1.8,
                }}
              >
                {note}
              </Typography>
            ))}
          </Box>
        </Paper>

        {/* Required agreement */}
        <Box
          sx={{
            textAlign: "center",
            mb: 2.5,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={form.agree}
                onChange={(event) => {
                  setForm((previous) => ({
                    ...previous,
                    agree: event.target.checked,
                  }));

                  setErrors((previous) => ({
                    ...previous,
                    agree: undefined,
                  }));
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: fonts[lang],
                  fontWeight: 600,
                }}
              >
                {text.agreeLabel}{" "}
                <Typography
                  component="span"
                  sx={{
                    color: "error.main",
                    fontFamily: fonts[lang],
                    fontWeight: 700,
                  }}
                >
                  {text.required}
                </Typography>
              </Typography>
            }
          />

          {!!errors.agree && (
            <Typography
              sx={{
                mt: 0.5,
                color: "error.main",
                fontFamily: fonts[lang],
                fontSize: 13,
              }}
            >
              {errorText("agree")}
            </Typography>
          )}
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || authLoading}
            aria-busy={submitting}
            startIcon={
              submitting ? <CircularProgress size={18} color="inherit" /> : null
            }
            sx={{
              minWidth: 260,
              px: 6,
              py: 1.1,

              fontFamily: fonts[lang],
              fontWeight: 700,

              bgcolor: accent,

              "&:hover": {
                bgcolor: alpha(accent, 0.85),
              },
            }}
          >
            {submitting ? text.submitting : text.submit}
          </Button>
        </Box>
      </Container>

      <SuccessSubmitDialog
        open={successOpen}
        onClose={handleSuccessClose}
        referenceValue={submittedRetailID}
        referenceLabel={RETAIL_SUCCESS_LABELS.reference}
        secondaryCloseOnly
        secondaryLabel={RETAIL_SUCCESS_LABELS.close}
        primaryHref={user ? "/線上報修/紀錄" : "/首頁"}
        primaryLabel={
          user ? RETAIL_SUCCESS_LABELS.history : RETAIL_SUCCESS_LABELS.home
        }
      />
    </Box>
  );
}
